import React, { useState, useMemo } from "react";
import { Card, Button, Col, Row, Pagination } from "react-bootstrap";
import "../../../styles/components/GenericCards.scss";

export interface CardConfigInterface {
  id: string;
  title: string;
  body?: string;
  image?: string | React.ReactNode;
  footer?: string;
  buttonText?: string;
  buttonAction?: (id: string) => void;
}

interface GenericCardsProps {
  cardsConfig: CardConfigInterface[];
  itemsPerPage?: number;
  enablePagination?: boolean;
}

const GenericCards = ({ cardsConfig, itemsPerPage = 8, enablePagination = true }: GenericCardsProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useMemo(() => {
    if (!enablePagination || cardsConfig.length === 0) return 1;
    return Math.ceil(cardsConfig.length / itemsPerPage);
  }, [cardsConfig.length, itemsPerPage, enablePagination]);

  const paginatedCards = useMemo(() => {
    if (!enablePagination || cardsConfig.length === 0) return cardsConfig;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return cardsConfig.slice(startIndex, endIndex);
  }, [cardsConfig, currentPage, itemsPerPage, enablePagination]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to page 1 when cardsConfig changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [cardsConfig.length]);

  return (
    <div className="generic-cards-container">
      <div className="generic-cards-scrollable-container">
        <Row xs={1} md={3} lg={4} className="g-0">
          {paginatedCards.map((card, index) => (
            <Col key={index} className="d-flex justify-content-center" style={{ flex: '0 0 auto', width: 'auto' }}>
              <Card
                className="custom-card"
                onClick={() => {
                  card.buttonAction &&
                    card.buttonAction(card.id || String(index));
                }}
              >
                {card.image &&
                  (typeof card.image === "string" ? (
                    <Card.Img variant="top" src={card.image} />
                  ) : (
                    <div className="svg-container">{card.image}</div>
                  ))}
                <Card.Body>
                  <Card.Title>{card.title}</Card.Title>
                  {card.body && <Card.Text>{card.body}</Card.Text>}
                  {card.buttonText && card.buttonAction && (
                    <Button variant="primary">{card.buttonText}</Button>
                  )}
                </Card.Body>
                {card.footer && <Card.Footer>{card.footer}</Card.Footer>}
              </Card>
            </Col>
          ))}
        </Row>
      </div>
      {enablePagination && totalPages > 1 && (
        <div className="generic-cards-pagination">
          <Pagination className="justify-content-center">
            <Pagination.First 
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              // Show first page, last page, current page, and pages around current
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (
                page === currentPage - 2 ||
                page === currentPage + 2
              ) {
                return <Pagination.Ellipsis key={page} />;
              }
              return null;
            })}
            <Pagination.Next 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last 
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default GenericCards;
